import type { CategoryContent } from '../row.js';

/**
 * East Asian Development, second chunk. Correct option first; rotated at build.
 * Kept strictly to 1980-2000 economic/development content - see EAST_ASIA in
 * asia.ts for the rest. Content outside that window moved to East Asian
 * History (asia2.ts).
 */
export const MORE_EAST_ASIA: CategoryContent = {
  msc: [
    [
      'The Special Economic Zones established in China from 1980 included which city?',
      [
        'Shenzhen',
        'Chongqing, as the inland pilot',
        'Tianjin, as the northern counterpart to Shanghai',
        'Dalian, opened under the coastal cities programme',
      ],
      0,
      'Shenzhen, Zhuhai, Shantou and Xiamen came first. Dalian and Tianjin were opened later, under the 1984 coastal cities decision.',
    ],
    [
      'The Chinese household responsibility system did what?',
      [
        'Assigned output responsibility to households while land remained collectively owned',
        'Privatised agricultural land outright to individual farming households',
        'Consolidated communes into larger state farms with wage labour',
        'Introduced compulsory grain procurement at market prices',
      ],
      0,
      'Rolled out from 1979-83. It produced the single largest agricultural output jump in the reform period and left the ownership question deliberately unresolved.',
    ],
    [
      "Japan's \"bubble economy\" peaked in which year?",
      ['1989', '1985', '1993', '1997'],
      0,
      'The Nikkei peaked in December 1989 and land prices about a year later. 1985 is the Plaza Accord, 1997 the banking crisis.',
    ],
    [
      'The Korean 1997-98 IMF programme required what, most controversially?',
      [
        'High interest rates and rapid financial restructuring alongside capital-account opening',
        'Immediate devaluation of the won against the dollar and yen',
        "Nationalisation of the chaebol's manufacturing assets, with subsequent resale to foreign buyers",
        'Withdrawal from the WTO accession process pending reform',
      ],
      0,
      "The pro-cyclical fiscal and monetary conditionality is what the subsequent literature - including from inside the Fund - treats as a mistake.",
    ],
    [
      'What does "state-owned enterprise reform" in 1990s China refer to?',
      [
        'The "grasp the large, let go of the small" policy',
        'Full privatisation of all industrial enterprises above a size threshold',
        'The transfer of enterprise ownership to their employees',
        'The conversion of state enterprises into township and village enterprises',
      ],
      0,
      "Zhu Rongji's restructuring. The scale of the layoffs is easy to lose next to the growth figures for the same years.",
    ],
    [
      'Hong Kong established its US-dollar currency peg (the Linked Exchange Rate System) in which year?',
      ['1983', '1997', '1990', '1978'],
      0,
      "The Hong Kong dollar was pegged at 7.80 to the US dollar in October 1983 during a currency crisis. It survived the 1997-98 Asian financial crisis intact, unlike almost every other regional currency, though adjustment happened through prices rather than the exchange rate.",
    ],
    [
      "Singapore's response to its 1985-86 recession, its first since independence, centered on what policy?",
      [
        'A cut in employer CPF contributions to lower labor costs and restore export competitiveness',
        "A devaluation of the Singapore dollar against the US dollar to boost export competitiveness",
        'Nationalization of loss-making manufacturing firms to prevent mass layoffs',
        "Introduction of Singapore's first statutory minimum wage to support household demand",
      ],
      0,
      "Singapore diagnosed its 1985-86 recession as a cost problem and cut the employer CPF contribution rate from 25% down toward 10%. The CPF, ordinarily forced-saving, functioned as a macroeconomic instrument because wage costs were treated as a state-adjustable variable. The episode reinforced Singapor",
    ],
    [
      "Taiwan's Hsinchu Science-Based Industrial Park, the model for the island's shift into semiconductors, was founded in which year?",
      ['1980', '1987', '1973', '1995'],
      0,
      "Hsinchu Science Park opened in 1980 to attract returning US-trained engineers to found semiconductor and electronics firms. It offered tax holidays, low-interest loans, and streamlined customs. The park embodied a state strategy to move Taiwan up the value chain, funded partly through Taiwanese-Amer",
    ],
    [
      'What did the 14th Congress of the Chinese Communist Party formally endorse in 1992?',
      [
        'Naming a "socialist market economy" the reform\'s official goal',
        'Abolishing the household registration (hukou) system nationwide',
        'Full privatization of all state-owned enterprises within five years',
        'A fixed timetable for full renminbi capital-account convertibility',
      ],
      0,
      "The 14th Party Congress in October 1992 formally endorsed Jiang Zemin's report naming a 'socialist market economy' as the explicit goal of reform, resolving years of ideological ambiguity over whether markets were a temporary expedient or a permanent feature of Chinese socialism.",
    ],
    [
      "Malaysia's response to the Asian financial crisis, imposed in September 1998, consisted of what?",
      [
        'A fixed exchange rate and capital controls pegging the ringgit to the dollar',
        'An IMF standby program with conventional fiscal and monetary conditionality',
        'Floating the ringgit freely while sharply raising interest rates to defend it',
        'Nationalization of the entire domestic banking sector',
      ],
      0,
      "In September 1998, Mahathir Mohamad's government pegged the ringgit at 3.80 to the dollar and banned offshore trading in the currency, defying both the IMF orthodoxy applied to Thailand, Indonesia and Korea and most mainstream economic advice at the time.",
    ],
  ],
  phd: [
    [
      'The Chinese "dual-track" price reform strategy did what?',
      [
        'Kept planned quotas at plan prices while allowing above-quota output to trade at market prices',
        'Set separate exchange rates for trade and capital transactions',
        'Applied different tax rates to state and non-state enterprises',
        'Maintained separate wage scales for urban and rural workers, set administratively by the labour ministry',
      ],
      0,
      "Lau, Qian and Roland argued it could be Pareto-improving without the rent destruction of a single-step liberalisation - the reference case against shock therapy.",
    ],
    [
      'Japanese main-bank monitoring is argued to have weakened for what reason?',
      [
        'Capital-market liberalisation let the best borrowers fund themselves directly, leaving banks the worst',
        "The Bank of Japan's window guidance was abolished in the mid-1980s",
        'Cross-shareholding was prohibited by the revised commercial code, forcing banks to sell their client equity stakes',
        'Main banks were required to divest their equity stakes under Basel I',
      ],
      0,
      'Adverse selection in the loan book is the mechanism usually named for how the bubble-era lending got so bad.',
    ],
    [
      'The Korean "big deals" of 1998 involved what?',
      [
        'State-brokered swaps of business lines between chaebol to reduce overcapacity',
        'The sale of chaebol subsidiaries to foreign strategic investors',
        'Debt-for-equity conversions at the state-owned banks',
        'The break-up of the five largest groups into independent firms',
      ],
      0,
      'Semiconductors, petrochemicals, aerospace. It is the clearest case of a supposedly liberalising government still directing industrial structure.',
    ],
    [
      "The Plaza Accord's consequences for Japan are usually described how?",
      [
        'Sharp yen appreciation, monetary easing in response',
        'A collapse in exports that triggered an immediate recession',
        'A rapid decline in the current account surplus to balance',
        'A permanent relocation of manufacturing to North America',
      ],
      0,
      'The causal chain from endaka to easing to bubble is contested in its details, but the sequence itself is not.',
    ],
    [
      'What was the "East Asian Miracle" report\'s central and most criticised claim?',
      [
        'That growth was largely explained by fundamentals',
        'That industrial policy was the primary driver of growth throughout the region',
        "That the region's growth was unsustainable without financial liberalisation",
        'That inequality necessarily rose with rapid growth in all eight cases',
      ],
      0,
      "The 1993 World Bank report. Wade and Amsden read it as reading the evidence back into the institution's existing priors.",
    ],
    [
      "Kia Motors' financial collapse in July 1997 mattered to the Korean crisis mainly because it did what?",
      [
        'Exposed how fragile debt-financed corporate leverage had become before contagion arrived',
        'Marked the first Korean firm formally nationalized under the December IMF program',
        "Triggered the won's initial managed float in March 1997, months before Thailand's crisis",
        'Caused the immediate collapse of the merchant banking sector ahead of any government response',
      ],
      0,
      "Kia Motors' request for emergency loans in July 1997, followed by effective bankruptcy that autumn, mattered because it was a large, non-marginal chaebol failure that showed how much debt-financed corporate expansion had outrun any plausible return on capital, before the regional contagion from Thai",
    ],
    [
      'Japan\'s "jusen" crisis of the mid-1990s refers to what?',
      [
        'Resolving failed non-bank housing lenders whose losses were split among banks, farm co-ops and the state',
        "The failure of the postal savings system's own investment arm",
        "The direct nationalization of Japan's major city banks",
        'A government bailout of consumer credit companies (sarakin)',
      ],
      0,
      "The jusen were specialized housing-loan companies set up by banks in the 1970s that, starved of ordinary mortgage business once banks entered that market themselves, diverted heavily into real-estate speculation during the bubble years and collapsed with it.",
    ],
    [
      'The Recruit scandal of 1988-89 in Japan is significant to bubble-economy history mainly because it revealed what?',
      [
        'How pre-IPO share allocations to politicians and officials bought influence amid land speculation',
        'That MITI had systematically falsified industrial output statistics through the 1980s',
        "That the Bank of Japan's discount window had funded a political slush fund",
        'That keiretsu cross-shareholding had breached the Anti-Monopoly Law',
      ],
      0,
      "The Recruit scandal broke in 1988 when it emerged that the conglomerate Recruit Cosmos had distributed unlisted shares, guaranteed to gain enormously once floated, to more than 90 politicians, bureaucrats and business figures, mostly as inducements tied to Recruit's real-estate and telecoms interest",
    ],
    [
      'The US-Japan Structural Impediments Initiative (1989-90) targeted what, distinct from earlier sector-specific trade disputes?',
      [
        "Japan's domestic structures — distribution, land use, keiretsu ties — as root causes of the trade imbalance",
        'Japanese tariff levels on manufactured imports specifically',
        'The yen-dollar exchange rate mechanism directly',
        "Japan's agricultural import quotas in isolation",
      ],
      0,
      "The Structural Impediments Initiative escalated from the sector-specific disputes of the early-to-mid 1980s (autos, semiconductors) to an argument that Japan's entire domestic economic architecture — multi-layered wholesale distribution, restrictive land-use and large-store laws, keiretsu supplier t",
    ],
    [
      "China's 1988 attempt at comprehensive price reform failed to produce sustained liberalization mainly because of what?",
      [
        'Its announcement triggered panic buying and deposit runs that fed inflation, forcing a retreat',
        'The coastal SEZs refused to adopt the new price schedules',
        'Provincial governments blocked the reform through unified fiscal resistance',
        'A State Council veto reversed the reform before it was implemented',
      ],
      0,
      "When the leadership signaled in mid-1988 that comprehensive price liberalization was coming, households responded with panic buying of everything from salt to televisions and rushed to withdraw bank deposits, fearing savings would be wiped out before prices were freed — a self-fulfilling dynamic tha",
    ],
    [
      "The unification of China's exchange rate system in January 1994 did what specifically?",
      [
        'Merged the official and swap-center rates, devaluing the yuan to about 8.7 per dollar',
        'Pegged the yuan to a currency basket for the first time',
        'Ended the foreign exchange certificate system for tourists only',
        'Granted full current-account convertibility to Chinese corporations',
      ],
      0,
      "Until January 1994, China ran a dual exchange-rate system — an overvalued official rate used for state-plan transactions and a much weaker market-clearing rate available at swap centers for everything else — and the 1994 reform scrapped the official rate, unifying near the swap-center level around 8",
    ],
    [
      'The December 1997 IMF-led rescue package for South Korea totaled roughly how much, and what distinguished its structure?',
      [
        'About $57bn, combining IMF, World Bank and ADB funds with a bilateral "second line of defense"',
        "About $17bn, funded solely through the IMF's standard stand-by facility",
        'About $40bn, disbursed entirely as unconditional budget support',
        'About $100bn, funded directly by G7 central banks outside the IMF',
      ],
      0,
      "The December 1997 Korea package totaled roughly $57bn, then the largest IMF-led rescue ever assembled, combining a $21bn IMF stand-by arrangement with World Bank and Asian Development Bank funds and a novel 'second line of defense' of bilaterally pledged, contingent reserves from countries including",
    ],
  ],
  professor: [
    [
      'The Young and Krugman critique of East Asian growth argued what?',
      [
        'Growth was driven by factor accumulation rather than total factor productivity gains',
        'Growth statistics were systematically overstated by state statistical agencies',
        'Growth depended on terms-of-trade gains that could not persist',
        'Growth was concentrated in non-tradable sectors and so could not continue',
      ],
      0,
      "Krugman's \"The Myth of Asia's Miracle\" (1994) popularised Young's and Kim's accounting. Later work with better capital-quality adjustment softened it considerably.",
    ],
    [
      'The Japanese "zombie firm" literature identifies what mechanism?',
      [
        'Bank forbearance kept unviable firms alive, congesting markets and depressing entrants\' investment',
        'Firms hoarded cash rather than investing because of deflation expectations',
        'Public works spending crowded out private investment in the same sectors',
        'Lifetime employment prevented labour reallocation between sectors or regions, so displaced workers stayed in place',
      ],
      0,
      'Caballero, Hoshi and Kashyap. The important claim is the externality: the damage is to the healthy firms, not the propped-up ones.',
    ],
    [
      'The Chinese "reform without losers" characterisation of the 1980s is challenged how?',
      [
        'The 1990s restructuring imposed very large losses',
        'The rural reforms reduced peasant incomes from the outset',
        'Price liberalisation caused an immediate fall in urban real wages',
        'The special economic zones drew investment away from existing industry',
      ],
      0,
      "Lau, Qian and Roland's formulation applied to the first decade. Naughton's point is that the accounting looks different once you include the SOE layoffs.",
    ],
    [
      'What is the significance of Japan\'s 1990s "convoy system" breakdown for financial regulation?',
      [
        'It ended the practice of arranging rescues so no institution visibly failed',
        'It transferred bank supervision from the Bank of Japan to the finance ministry',
        'It introduced deposit insurance for the first time',
        'It required banks to mark loan books to market annually',
      ],
      0,
      'The Financial Supervisory Agency and the 1998 resolution framework are the institutional residue. Deposit insurance long predated it.',
    ],
    [
      'What does the "developmental state in decline" argument claim about Japan after 1990?',
      [
        'The institutions that coordinated catch-up became liabilities once the frontier was reached',
        'Bureaucratic capacity collapsed as recruitment from elite universities fell',
        'Industrial policy was abandoned entirely under external pressure',
        'The state lost control of the banking system to foreign shareholders',
      ],
      0,
      "The general form of the claim - that catch-up institutions and frontier institutions are different - is also how the same literature reads China's current problem.",
    ],
    [
      "The Hong Kong Monetary Authority's August 1998 intervention in the stock and futures markets is significant because of what?",
      [
        'It was a currency board buying equities directly, to counter a "double play" of currency and stock speculation',
        'It marked abandonment of the linked exchange rate in favor of a managed float',
        "It was coordinated jointly with the People's Bank of China to defend the renminbi",
        'It followed IMF conditionality attached to a standby loan for Hong Kong',
      ],
      0,
      "In August 1998 the Hong Kong Monetary Authority spent roughly HK$118bn buying blue-chip stocks and index futures directly, an extraordinary intervention for an institution built as a currency board whose entire design principle was not to exercise discretionary market power.",
    ],
    [
      'The debate over whether the 1997-98 Asian crisis was driven by "fundamentals" or "panic" turned significantly on which empirical point?',
      [
        'Whether short-term dollar liabilities dwarfed reserves enough to make a self-fulfilling run possible',
        'Whether current account deficits alone predicted which countries were hit',
        'Whether fixed exchange rates were present in every crisis country without exception',
        'Whether capital controls had been lifted in each country during the same calendar year',
      ],
      0,
      "Radelet and Sachs's 'panic' account argued the crisis countries were victims of a liquidity run rather than genuinely insolvent, pointing to short-term foreign debt figures that dwarfed usable reserves — meaning coordinated withdrawal by creditors could become self-fulfilling even if the underlying",
    ],
    [
      "The 'moral hazard' account of the pre-1997 East Asian investment boom, developed by economists including Ronald McKinnon and Huw Pill and echoed in Paul Krugman's 1998 essay on the crisis, located the fundamental problem in what?",
      [
        'Implicit government guarantees to banks and favored firms, which let them borrow and invest as if downside risk were socialized',
        'Explicit deposit insurance schemes introduced too early in the financial liberalization sequence',
        'The near-total absence of government involvement in bank credit allocation, leaving investment risk unpriced',
        'Monetary tightening that pushed domestic banks into riskier unhedged foreign-currency borrowing',
      ],
      0,
      "McKinnon and Pill, and separately Krugman, argued that the region's investment boom was fueled by implicit guarantees: banks and the large firms they funded believed, correctly as it turned out in several bailouts, that the state would absorb losses if things went wrong, so they took on leverage and",
    ],
    [
      "Indonesia's negotiations with the IMF in early 1998 broke down repeatedly over which specific proposal from Suharto's advisers?",
      [
        'A currency board pegging the rupiah at around 5,000 to the dollar, championed by Steve Hanke',
        'A full float of the rupiah with no reserve floor whatsoever',
        'Outright nationalization of the private banking sector',
        'A debt moratorium covering both sovereign and private external debt',
      ],
      0,
      "In early 1998, with the rupiah in freefall, Suharto's advisers — most visibly American economist Steve Hanke — pushed a currency-board scheme fixing the rupiah rigidly against the dollar, reasoning that a hard peg backed by full reserve coverage would restore confidence the way it had for Hong Kong",
    ],
    [
      "China's 1993 Company Law mattered to state-owned enterprise reform mainly because it did what?",
      [
        'Gave state enterprises a legal form — shareholding and limited-liability companies — for 1990s corporatization',
        'Imposed for the first time a legal ceiling on state ownership in listed firms',
        "Extended the household responsibility system's logic formally to urban state firms",
        "Created a National People's Congress approval requirement for closing any large enterprise",
      ],
      0,
      "China's 1993 Company Law supplied, for the first time, a general legal framework for limited-liability and joint-stock companies, which mattered because 1990s SOE reform increasingly took the form of 'corporatization' — converting an enterprise into a company with a board and shareholders, sometimes",
    ],
    [
      "Chun Doo-hwan's early-1980s economic stabilization program in Korea marked what turn relative to the Park Chung-hee era?",
      [
        'A shift from growth-at-all-costs heavy-industry drives toward stabilization and gradual liberalization',
        "Nationalization of the chaebol's core manufacturing assets",
        'Abandonment of export promotion in favor of import substitution',
        "Introduction of Korea's first five-year plan, replacing ad hoc targeting",
      ],
      0,
      "Coming to power after Park's assassination and amid the fallout from the debt-financed Heavy and Chemical Industry drive of the late 1970s, which had produced serious overcapacity and inflation, Chun Doo-hwan's economic team — led by technocrats like Kim Jae-ik — pivoted in the early 1980s toward ti",
    ],
    [
      'The founding of the Shanghai and Shenzhen stock exchanges in 1990-91 is often read by historians as significant for what specific reason?',
      [
        'They preceded, rather than followed, developed corporate law and clear property rights — reversing the usual sequence',
        'They were the first exchanges anywhere to trade solely foreign-currency-denominated shares',
        'They immediately allowed full foreign ownership of all listed A-shares',
        'They were established explicitly to finance the Three Gorges Dam project',
      ],
      0,
      "The Shanghai and Shenzhen exchanges opened in December 1990 and 1991 respectively, years before China had a general company law (1993), a securities law (1998), or settled answers about what property rights a 'share' actually conferred against a nominally state-owned enterprise.",
    ],
    [
      "Japan's April 1997 increase in the consumption tax from 3% to 5% under Prime Minister Hashimoto Ryutaro is significant in macroeconomic debate mainly because of what?",
      [
        'It is widely blamed for choking off a fragile 1996 recovery just as bank failures and the Asian crisis hit',
        'It was paired with an equivalent income-tax cut, making the net fiscal stance neutral by design',
        'It was earmarked entirely to fund the jusen bailout, ring-fencing the cost from general revenue',
        'It was reversed within a year after business-lobby pressure, before it affected consumption',
      ],
      0,
      "Hashimoto's government raised the consumption tax from 3% to 5% in April 1997, at a moment when Japan's recovery from the early-1990s slump was still fragile, and most subsequent accounts treat the tax hike, combined with the simultaneous expiry of an earlier income-tax cut and reduced public works",
    ],
    [
      "Zhao Ziyang's coastal development strategy, announced in 1988, is distinguished from the earlier Special Economic Zone policy by what shift in emphasis?",
      [
        'Extending SEZ-style export and investment treatment along the whole coast, not just a handful of enclaves',
        'Replacing foreign direct investment with domestic private capital as the primary financing source',
        'Abolishing the special tax treatment that had defined the original SEZs',
        'Shifting the reform\'s geographic focus inland to the Yangtze basin, ahead of the coast',
      ],
      0,
      "Zhao Ziyang's coastal development strategy generalized the SEZ model of preferential tax treatment, streamlined approvals and export orientation to a much larger swath of coastal China — roughly 160 million people across Guangdong, Fujian, Jiangsu, Zhejiang, Shandong and other provinces — rather tha",
    ],
  ],
};
